import { useState } from 'react'
import { Alert, Button, Dropdown, Form, Input, Modal, Select } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, notify, getFriendlyErrorMessage } from '@kolab/dms-base/lib'
import {
  activateDevice,
  deactivateDevice,
  listDeviceLicenses,
  revokeDevice,
  wipeDevice,
} from '../api/deviceLicense.api'
import { deviceLicenseKeys } from '../api/queryKeys'
import type { DeviceLicense, DeviceStatus } from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { P, usePermissionStore } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'

const { confirm } = Modal

const STATUS_OPTIONS: { value: '' | DeviceStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'ACTIVE' },
  { value: 'INACTIVE', label: 'INACTIVE' },
  { value: 'BLOCKED', label: 'BLOCKED' },
  { value: 'REVOKED', label: 'REVOKED' },
]

export default function DeviceLicensesPage() {
  const [statusFilter, setStatusFilter] = useState<'' | DeviceStatus>('')
  const queryClient = useQueryClient()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<DeviceLicense>({
    queryKey: deviceLicenseKeys.list({ pageSize: 20, status: statusFilter || undefined }),
    queryFn: (params) => listDeviceLicenses(params),
    params: { status: statusFilter || undefined },
    pageSize: 20,
  })

  const [activateOpen, setActivateOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  const { hasPermission } = usePermissionStore()

  const activateMut = useMutation({
    mutationFn: activateDevice,
    onSuccess: (res) => {
      toast.success(`Device activated (${res.action})`)
      queryClient.invalidateQueries({ queryKey: deviceLicenseKeys.all })
      setActivateOpen(false)
    },
    onError: (error: unknown) => {
      handleFormError(error, 'Failed to activate device')
    },
  })

  const deactivateMut = useMutation({
    mutationFn: deactivateDevice,
    onSuccess: () => {
      toast.success('Device deactivated')
      queryClient.invalidateQueries({ queryKey: deviceLicenseKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to deactivate device'))
    },
  })

  const revokeMut = useMutation({
    mutationFn: revokeDevice,
    onSuccess: () => {
      toast.success('Device revoked')
      queryClient.invalidateQueries({ queryKey: deviceLicenseKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      notify.error({
        message: 'Revoke failed',
        description: getFriendlyErrorMessage(error, 'Failed to revoke device. The device license was not removed.'),
      })
    },
  })

  const wipeMut = useMutation({
    mutationFn: wipeDevice,
    onSuccess: () => {
      toast.success('Device wiped')
      queryClient.invalidateQueries({ queryKey: deviceLicenseKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      notify.error({
        message: 'Wipe failed',
        description: getFriendlyErrorMessage(error, 'Failed to wipe device credentials. The device keys were not cleared.'),
      })
    },
  })

  const columns: ColumnsType<DeviceLicense> = [
    { title: 'Device identifier', dataIndex: 'deviceIdentifier', key: 'deviceIdentifier' },
    {
      title: 'Reseller',
      dataIndex: 'resellerName',
      key: 'resellerName',
    },
    { title: 'Sub-license', dataIndex: 'resellerLicenseId', key: 'resellerLicenseId' },
    { title: 'Expiry', dataIndex: 'deviceExpiryDate', key: 'deviceExpiryDate' },
    { title: 'First activated', dataIndex: 'firstActivatedAt', key: 'firstActivatedAt' },
    { title: 'Last seen', dataIndex: 'lastSeenAt', key: 'lastSeenAt' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const items = [
          hasPermission(P.device_licenses.device.deactivate) && {
            key: 'deactivate',
            label: 'Deactivate',
            onClick: () =>
              confirmAction(`Deactivate ${record.deviceIdentifier}?`, () => deactivateMut.mutateAsync(record.id)),
          },
          hasPermission(P.device_licenses.device.revoke) && {
            key: 'revoke',
            label: 'Revoke',
            danger: true,
            onClick: () =>
              confirmAction(
                `Revoke ${record.deviceIdentifier}? This is not reversible.`,
                () => revokeMut.mutateAsync(record.id),
              ),
          },
          hasPermission(P.device_licenses.device.wipe) && {
            key: 'wipe',
            label: 'Wipe credentials',
            danger: true,
            onClick: () =>
              confirmAction(
                `Wipe device credentials for ${record.deviceIdentifier}? This clears its keys so the device can re-register.`,
                () => wipeMut.mutateAsync(record.id),
              ),
          },
        ].filter(Boolean) as { key: string; label: string; onClick: () => void; danger?: boolean }[]

        if (items.length === 0) return null
        return (
          <Dropdown trigger={['click']} menu={{ items }} getPopupContainer={() => document.body}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  return (
    <PageLayout
      // actions={
      //   <Can permission={P.device_licenses.device.activate}>
      //     <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => setActivateOpen(true)}>
      //       Activate device
      //     </Button>
      //   </Can>
      // }
      filters={
        <Select
          style={{ minWidth: 180 }}
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
        />
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<DeviceLicense>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <ActivateDeviceModal
        open={activateOpen}
        onCancel={() => { setActivateOpen(false); setFormError(null) }}
        onSubmit={(fields) => activateMut.mutate(fields)}
        submitting={activateMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

function confirmAction(title: string, run: () => Promise<unknown>) {
  confirm({
    title,
    okText: 'Continue',
    okButtonProps: { danger: true },
    onOk: () => run().catch(() => {}),
  })
}

function ActivateDeviceModal({
  open,
  onCancel,
  onSubmit,
  submitting,
  error,
  onClearError,
}: {
  open: boolean
  onCancel: () => void
  onSubmit: (fields: {
    subLicenseKey: string
    deviceIdentifier: string
    deviceId?: string
    endCustomerId?: string
  }) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm()

  return (
    <Modal
      open={open}
      title="Activate device"
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form form={form} layout="vertical" preserve={false} onFinish={onSubmit}>
        <Form.Item name="subLicenseKey" label="Reseller sub-license key" rules={[{ required: true }]}>
          <Input placeholder="Paste the key issued to the reseller" />
        </Form.Item>
        <Form.Item name="deviceIdentifier" label="Device identifier" rules={[{ required: true }]}>
          <Input placeholder="Serial number / MAC / device-provided ID" />
        </Form.Item>
        <Form.Item name="deviceId" label="Cross-ref device ID (optional)">
          <Input placeholder="MQTT device id if applicable" />
        </Form.Item>
        <Form.Item name="endCustomerId" label="End customer ID (optional)">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export { DeviceLicensesPage }
